import { FC, useContext, useEffect, useRef } from "react"

import { CheckCircleTwoTone } from "@ant-design/icons"
import { OidcSecure } from "@axa-fr/react-oidc"
import { Button, Layout, Typography } from "antd"
import { NavLink, Outlet } from "react-router-dom"

import { Header } from "@/components/_Shared/Layout/Header"
import { Sidebar } from "@/components/_Shared/Layout/Sidebar"
import { Resource, Scope } from "@/constants/permission"
import { CustomerContext } from "@/context/CustomerContext"
import { useNotificationContext } from "@/context/NotificationContext"
import { useSignalR } from "@/context/SignalRContext"
import { useTimeCoordinateContext } from "@/context/TimeCoordinateContext"
import { WithSuspense } from "@/hoc/WithSuspense"

import classes from "./MainLayout.module.scss"

import { useNotificationMutation } from "@/hook/Notifications/useNotificationMutation"
import { usePermission } from "@/hook/usePermission"

import { INotification, NotificationType } from "@/types/INotification"

import { paths } from "@/router/paths"

const { Text } = Typography


export const MainLayout: FC = () => {
  const connection = useSignalR()
  const notificationAudio = useRef<HTMLAudioElement>(null)
  const customer = useContext(CustomerContext)

  const { hasPermission } = usePermission()
  const { updateReadStatusForNotificationsMutation } = useNotificationMutation()
  const { refetchNotifications, notificationApi, notificationContextHolder } = useNotificationContext()
  const { refetchTimeCoordinateOrders } = useTimeCoordinateContext()

  useEffect(() => {
    showNotReadNotificatons()

    connection?.on("ReciveNotification", (notification: INotification) => {
      if (notification.isVoiceEnable){
        notificationAudio.current!.muted = false
        notificationAudio!.current!.currentTime = 0
        notificationAudio.current?.play().catch(error => {
          // eslint-disable-next-line no-console
          console.log("Воспроизведение заблокировано:", error)
        })
      }

      if (hasPermission(Resource.DispatcherTimeCoordinate, Scope.Menu)) {
        refetchTimeCoordinateOrders()
      }

      notificationApi.info({
        className: classes["notification-card"],
        icon: <img alt="notification-icon" src="/notification-icon.svg" height={24} width={24} />,
        message: <Text
          className={classes["notification-card-text"]}
          type={notification.notificationType === NotificationType.informationAllUsers ? "danger" : undefined}>
          {processNotificationContent(notification, customer.isCustomer(), hasPermission)}
        </Text>,
        btn: <>
          <Button type="link" onClick={()=> readAndCloseNotification(notification.id)}>
            <CheckCircleTwoTone /> Прочитано
          </Button></>,
        placement: "bottomRight",
        duration: 45,
        key: notification.id,
        onClose: () => closeNotification(notification.id)
      })
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, notificationApi])

  const showNotReadNotificatons = async () => {
    const notifications = await connection?.invoke<INotification[]>("GetNotReadNotification") ?? []

    for (const notification of notifications) {
      notificationApi.info({
        className: classes["notification-card"],
        icon: <img alt="notification-icon" src="/notification-icon.svg" height={24} width={24} />,
        message: <Text
          className={classes["notification-card-text"]}
          type={notification.notificationType === NotificationType.informationAllUsers ? "danger" : undefined}>
          {processNotificationContent(notification, customer.isCustomer(), hasPermission)}
        </Text>,
        btn: <>
          <Button type="link" onClick={()=> readAndCloseNotification(notification.id)}>
            <CheckCircleTwoTone /> Прочитано
          </Button></>,
        placement: "bottomRight",
        duration: 45,
        key: notification.id,
        onClose: () => closeNotification(notification.id)
      })
    }
  }

  const closeNotification = (idNotification: number) => {
    notificationApi.destroy(idNotification)
    refetchNotifications()
  }

  const readAndCloseNotification = async (idNotification: number) => {
    await updateReadStatusForNotificationsMutation.mutateAsync({
      ids: [idNotification],
      isRead: true
    })

    notificationApi.destroy(idNotification)
    refetchNotifications()
  }

  return (
    <Layout>
      <Sidebar/>
      <Layout className={classes.layout}>
        <Header/>
        <Layout.Content className={classes.content}>
          <div className={classes["page-wrapper"]}>
            {notificationContextHolder}
            <audio ref={notificationAudio} src="notification-sound.mp3" muted autoPlay/>
            <WithSuspense>
              <OidcSecure>
                <Outlet/>
              </OidcSecure>
            </WithSuspense>
          </div>
        </Layout.Content>
      </Layout>
    </Layout>)
}

const LinksNotificationTypes =
  [
    NotificationType.changeOrderStatus,
    NotificationType.updateDriverCatalog,
    NotificationType.updateTransportCatalog,
    NotificationType.UpdateAddressCatalog,
    NotificationType.UpdateFuelLimit,
    NotificationType.UpdateFuelPrice,
    NotificationType.NewReturnFundRequest,
    NotificationType.UpdateReturnFundRequest,
    NotificationType.RedFlagOrderType
  ]

export const processNotificationContent = (
  notification: INotification,
  isCustomer: boolean,
  hasPermissionFunc: (resource: Resource, scope: Scope) => boolean) => {
  const message = notification.message

  if (!(LinksNotificationTypes.includes(notification.notificationType)))
    return message

  if (!message.includes("*"))
    return message

  const messageSplit = message.split("*")

  if (message.length < 3)
    return message

  let mainLinkPath
  let hasPermission = false

  const entityId = Number(notification.entityId) ?? 0

  const getOrderLinkPath = (): string => {

    if (hasPermissionFunc(Resource.Order, Scope.Menu)) {
      return paths.orderById(entityId)
    }
    if (hasPermissionFunc(Resource.DispatcherOrders, Scope.Menu)) {
      return paths.dispatcherOrderById(entityId)
    }
    if (hasPermissionFunc(Resource.DispatcherTimeCoordinate, Scope.Menu)) {
      return paths.dispatcherTimeCoordinateById(entityId)
    }
    if (isCustomer) {
      return paths.ordersHistoryById(entityId)
    }
    return ""
  }

  switch (notification.notificationType) {
    case NotificationType.changeOrderStatus:
    case NotificationType.RedFlagOrderType:
      mainLinkPath = getOrderLinkPath()
      hasPermission = true
      break

    case NotificationType.updateTransportCatalog:
      mainLinkPath = paths.transportById(entityId)
      hasPermission = hasPermissionFunc(Resource.Transport, Scope.Menu)
      break

    case NotificationType.updateDriverCatalog:
      mainLinkPath = paths.driverById(entityId)
      hasPermission = hasPermissionFunc(Resource.Driver, Scope.Menu)
      break

    case NotificationType.UpdateAddressCatalog:
      mainLinkPath = paths.deliveryAddressById(entityId)
      hasPermission = hasPermissionFunc(Resource.DeliveryAddress, Scope.Menu)
      break

    case NotificationType.UpdateFuelLimit:
      mainLinkPath = paths.fuelLimitById(entityId)
      hasPermission = hasPermissionFunc(Resource.FuelLimit, Scope.Menu)
      break

    case NotificationType.UpdateFuelPrice:
      if (notification.entityId) {
        const idsSplit = notification.entityId.split(";")
        mainLinkPath = paths.fuelQuoteById(Number(idsSplit[0]), Number(idsSplit[1]))
      }
      hasPermission = hasPermissionFunc(Resource.Fuel, Scope.Menu)
      break

    case NotificationType.NewReturnFundRequest:
    case NotificationType.UpdateReturnFundRequest:
      mainLinkPath = paths.financeReturnFundsById(entityId)
      hasPermission = hasPermissionFunc(Resource.FinanceReturnFunds, Scope.Menu)
      break
  }

  return (
    <>
      {
        messageSplit[0]
      }
      {
        hasPermission
          ? <NavLink to={mainLinkPath ?? ""}>
            {messageSplit[1]}
          </NavLink>
          : <span>{messageSplit[1]}</span>
      }
      {
        messageSplit.slice(2).join("*")
      }
    </>
  )
}